import type { CSSProperties } from 'react'

import Link from 'next/link'
import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { PillButton } from '@/components/generals/pill-button'
import { useAuth } from '@/components/generals/auth-provider'
import { PRIMARY_NAV, SECONDARY_NAV } from '@/constants/navigation'

interface MobileMenuProps {
    open: boolean
    onClose: () => void
}

const LINKS = [...PRIMARY_NAV, ...SECONDARY_NAV]

/** Full-screen black overlay with large italic serif links. */
export const MobileMenu = ({ open, onClose }: MobileMenuProps) => {
    const { member } = useAuth()
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        if (!open) return

        const previous = document.body.style.overflow
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKey)
        closeRef.current?.focus()

        return () => {
            document.body.style.overflow = previous
            window.removeEventListener('keydown', onKey)
        }
    }, [open, onClose])

    return (
        <div
            id='mobile-menu'
            role='dialog'
            aria-modal='true'
            aria-label='Site menu'
            aria-hidden={!open}
            inert={!open}
            className={cn(
                'fixed inset-0 z-[60] flex flex-col bg-black transition-[opacity,visibility] duration-500 ease-cinema lg:hidden',
                open ? 'visible opacity-100' : 'invisible opacity-0'
            )}
        >
            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(70%_100%_at_50%_0%,rgba(143,174,98,0.22),transparent_70%)]' />

            <div className='relative flex items-center justify-between px-6 pt-6'>
                <Logo onClick={onClose} />

                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    aria-label='Close menu'
                    className='glass grid size-11 place-items-center rounded-full text-cream'
                >
                    <X className='size-5' />
                </button>
            </div>

            <nav aria-label='Mobile' className='relative flex-1 overflow-y-auto px-6 pt-12'>
                <ul className='space-y-1'>
                    {LINKS.map((item, index) => (
                        <li
                            key={item.href}
                            className={cn(
                                'transition-[opacity,transform] duration-700 ease-cinema',
                                open ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
                            )}
                            style={{ transitionDelay: open ? `${120 + index * 55}ms` : '0ms' } as CSSProperties}
                        >
                            <Link
                                href={item.href}
                                onClick={onClose}
                                className='block py-1.5 font-serif text-[2.6rem] italic leading-tight tracking-[-0.01em] text-cream/90 transition-colors hover:text-citron'
                            >
                                {item.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>

            <div
                className={cn(
                    'relative flex items-center justify-between gap-4 border-t border-white/10 px-6 py-6 transition-opacity delay-300 duration-700',
                    open ? 'opacity-100' : 'opacity-0'
                )}
            >
                <Link
                    href={member ? '/account' : '/login'}
                    onClick={onClose}
                    className='text-sm text-cream/70 underline decoration-cream/30 underline-offset-[6px]'
                >
                    {member ? 'Your account' : 'Login'}
                </Link>

                <PillButton href='/find-an-advisor' size='sm' onClick={onClose}>
                    Find an advisor
                </PillButton>
            </div>
        </div>
    )
}
